import React, { useEffect, useContext } from 'react'
import { useLazyQuery, NetworkStatus } from '@apollo/client'
import { Card, CardContent, Table, TableBody, TableCell, TableContainer, TableRow } from '@material-ui/core'
import { makeStyles, withStyles, createStyles, Theme } from '@material-ui/core/styles'
import { DailyMonitoringDetailsFiltersContext } from '../../../../context/DailyMonitoringDetailsFiltersContext'
import { GET_SUMARY_DATA } from '../../../../queries/dailyMonitoringCancelled'
import TableSkeleton from '../../../../components/skeletons/TableSkeleton'
import { formatNumber, formatCurrency } from '../../../../utils/Helpers'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      marginTop: theme.spacing(2)
    },
    rowHoverFixed: {
      '&:nth-of-type(even)': {
        backgroundColor: theme.palette.action.hover
      }
    }
  })
)

const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      backgroundColor: '#EF6167',
      color: theme.palette.common.white
    },
    body: {
      fontSize: 12,
      textAlign: 'center',
      '&.blue': {
        backgroundColor: '#9ED6EE'
      },
      '&.grey': {
        backgroundColor: '#B6B6B6',
        color: theme.palette.common.black
      },
      '&.negative': {
        color: '#f00'
      },
      '&.bold': {
        fontWeight: 'bold'
      },
      '&.bordered': {
        borderBottom: '1px solid #4B4B4B'
      },
      '&:first-child': {
        textAlign: 'left'
      }
    }
  })
)(TableCell)

type Props = {
  children: string
  className?: string
}

const CellNumber = ({ children, className }: Props): JSX.Element => {
  const isNegative = Math.sign(parseFloat(children)) === -1 ? true : children.indexOf('-') >= 0
  return <StyledTableCell className={`${className} ${isNegative && 'negative'}`}>{children}</StyledTableCell>
}

export default function CancelledSummary (): JSX.Element {
  const classes = useStyles()
  const { filters } = useContext(DailyMonitoringDetailsFiltersContext)
  const [getData, { loading, error, data, networkStatus }] = useLazyQuery(
    GET_SUMARY_DATA, {
      notifyOnNetworkStatusChange: true
    }
  )

  useEffect(() => {
    getData({
      variables: { filters }
    })
  }, [filters])

  if (networkStatus === NetworkStatus.refetch) return <TableSkeleton />
  if (loading || !data) return <TableSkeleton />
  if (error) return <p>Error :(</p>
  if (!data.dailyMonitoringCancelled) return <p>No data found</p>

  const {
    ordersCheckoutTotal,
    ordersCancelledTotal,
    ordersCancelledQuantity,
    ordersCancelledAverage,
    ordersCancelledPercentage
  } = data.dailyMonitoringCancelled

  return (
    <Card className={classes.root}>
      <CardContent>
        <TableContainer>
          <Table style={{ fontSize: 12 }}>
            <TableBody>
              <TableRow className={classes.rowHoverFixed}>
                <StyledTableCell className="bold bordered">GMV (Aberto)</StyledTableCell>
                <CellNumber className="blue bold bordered">{formatCurrency(ordersCheckoutTotal, 2)}</CellNumber>
              </TableRow>
              <TableRow className={classes.rowHoverFixed}>
                <StyledTableCell>GMV</StyledTableCell>
                <CellNumber className="blue bold bordered">{formatCurrency(ordersCancelledTotal, 2)}</CellNumber>
              </TableRow>
              <TableRow className={classes.rowHoverFixed}>
                <StyledTableCell>QTD Pedidos</StyledTableCell>
                <StyledTableCell colSpan={2} align="center" className="blue">{formatNumber(ordersCancelledQuantity, 0)}</StyledTableCell>
              </TableRow>
              <TableRow className={classes.rowHoverFixed}>
                <StyledTableCell className="bordered">Ticket Médio</StyledTableCell>
                <StyledTableCell colSpan={2} align="center" className="blue bordered">{formatCurrency(ordersCancelledAverage, 2)}</StyledTableCell>
              </TableRow>
              <TableRow className={classes.rowHoverFixed}>
                <StyledTableCell>% de cancelamento GMV</StyledTableCell>
                <StyledTableCell className="bold blue">{`${formatNumber(ordersCancelledPercentage, 2)}%`}</StyledTableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  )
}
